import React from 'react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { ParkingReservationDto } from 'Services/Api/_HumanWritten/ParkingService';
import { formatStandardDate } from 'Util/_HumanWritten/DateUtils';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { getMessageByKey } from 'Stores/LanguageStore';
import { Tag } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInList/Tag';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';

interface ParkingBookingCardProps {
	reservation: ParkingReservationDto;
}

function getStatusTag(
	status: string,
	vehicles?: { startDate: string; endDate: string }[],
): { text: string; tagClassName: string } {
	if (status === 'CONFIRMED' && vehicles && vehicles.length > 0) {
		const today = dayjs().startOf('day');
		const allCompleted = vehicles.every(v => dayjs(v.endDate).startOf('day').isBefore(today));
		const anyActive = vehicles.some(
			v => !dayjs(v.startDate).startOf('day').isAfter(today) && !dayjs(v.endDate).startOf('day').isBefore(today),
		);
		if (allCompleted) {
			return { text: 'Completed', tagClassName: 'grey' };
		}
		if (anyActive) {
			return { text: 'Active', tagClassName: 'tag--success' };
		}
	}
	switch (status) {
		case 'PENDING':
			return { text: 'Pending', tagClassName: 'tag--warning' };
		case 'CONFIRMED':
			return { text: 'Confirmed', tagClassName: 'tag--success' };
		case 'CANCELLED':
			return { text: 'Cancelled', tagClassName: 'tag--error' };
		case 'EXPIRED':
			return { text: 'Expired', tagClassName: 'grey' };
		default:
			return { text: status, tagClassName: 'grey' };
	}
}

export function ParkingBookingCard({ reservation }: ParkingBookingCardProps) {
	const vehicle = reservation.vehicles?.[0];
	const statusTag = getStatusTag(reservation.status, reservation.vehicles);
	const locationName = reservation.parkingLocation?.name || 'Parking';

	const rego = vehicle?.registration?.toUpperCase();
	const startDate = vehicle ? formatStandardDate(new Date(vehicle.startDate)) : '';
	const endDate = vehicle ? formatStandardDate(new Date(vehicle.endDate)) : '';
	const numDays = vehicle ? dayjs(vehicle.endDate).diff(dayjs(vehicle.startDate), 'day') + 1 : 0;

	const isPast =
		reservation.status === 'CANCELLED' ||
		reservation.status === 'EXPIRED' ||
		reservation.vehicles?.every(v => dayjs(v.endDate).startOf('day').isBefore(dayjs().startOf('day')));

	return (
		<Link to={`/parking-reservations/${reservation.id}`} className="booking-card-container hide-underline">
			<div className="booking">
				<div className="booking-header">
					<div className="booking-header__left">
						<div className="booking-header__icon booking-header__icon--parking">
							<Icon name="local_parking" useMaterialIcons />
						</div>
						<div className="booking-header__info">
							<div className="booking-header__type-row">
								<span className="booking-header__type">Parking</span>
								<span className="booking-header__id booking-header__id--mobile">
									#{reservation.humanReadableId}
								</span>
							</div>
							<div className="booking-header__title-row">
								<h4 className="booking-header__title">{locationName}</h4>
								<div className="booking-header__status--mobile">
									<Tag text={statusTag.text} tagClassName={statusTag.tagClassName} />
								</div>
							</div>
							<div className="booking-header__meta">
								<span>
									<Icon name="calendar_today" useMaterialIcons />
									{vehicle ? dayjs(vehicle.startDate).format('DD MMM YYYY') : ''}
								</span>
								<Icon name="arrow_forward" useMaterialIcons classname="booking-header__arrow" />
								<span>
									<Icon name="calendar_today" useMaterialIcons />
									{vehicle ? dayjs(vehicle.endDate).format('DD MMM YYYY') : ''}
								</span>
							</div>
						</div>
					</div>
					<div className="booking-header__right">
						<span className="booking-header__id booking-header__id--desktop">
							#{reservation.humanReadableId}
						</span>
						<div className="tags__container">
							<Tag text={statusTag.text} tagClassName={statusTag.tagClassName} />
						</div>
						<span className="booking-header__price">
							{formatPriceDisplay(reservation.totalPrice, false)}
						</span>
					</div>
				</div>
				<div className="ticket-body">
					{rego && (
						<div className="ticket-body__item">
							<p className="ticket-body__label">{getMessageByKey('vehicle-tab__rego__label', 'Rego')}</p>
							<p className="ticket-body__value">{rego}</p>
						</div>
					)}
					<div className="ticket-body__item">
						<p className="ticket-body__label">Duration</p>
						<p className="ticket-body__value">
							{numDays} {numDays === 1 ? 'day' : 'days'}
						</p>
					</div>
				</div>
			</div>
			<span className="view-booking-detail-section__edit-button">
				{isPast ? 'View reservation' : 'View/Edit reservation'}
			</span>
		</Link>
	);
}
